import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Divider,
  Chip,
  CircularProgress,
  Stack,
  Button,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Badge as BadgeIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  CalendarToday as CalendarIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { fetchUserProfile, logout } from '../../store/slices/authSlice';

const Row: React.FC<{ icon: React.ReactNode; label: string; value?: React.ReactNode }> = ({
  icon,
  label,
  value,
}) => (
  <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1.25 }}>
    <Box sx={{ color: 'text.secondary', display: 'flex' }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {value || value === 0 ? value : '—'}
      </Typography>
    </Box>
  </Stack>
);

const Profile: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user, profile, loading } = useAppSelector((state) => state.auth);

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  const fullName =
    user?.full_name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
    user?.username ||
    'User';

  const initials =
    (user?.first_name?.[0] || user?.username?.[0] || 'U').toUpperCase();

  if (loading && !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
        My Profile
      </Typography>

      <Grid container spacing={3}>
        {/* Identity card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <CardContent>
              <Avatar
                sx={{
                  width: 96,
                  height: 96,
                  bgcolor: 'primary.main',
                  fontSize: 36,
                  mx: 'auto',
                  mb: 2,
                }}
              >
                {initials}
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {fullName}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                @{user?.username}
              </Typography>
              {profile?.designation && (
                <Chip
                  label={profile.designation}
                  color="primary"
                  size="small"
                  sx={{ mt: 1 }}
                />
              )}
              <Divider sx={{ my: 2 }} />
              <Button
                fullWidth
                variant="outlined"
                color="error"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
              >
                Logout
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Details */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Account Information
              </Typography>
              <Divider />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Row icon={<PersonIcon />} label="Full Name" value={fullName} />
                  <Row icon={<BadgeIcon />} label="Username" value={user?.username} />
                  <Row icon={<EmailIcon />} label="Email" value={user?.email} />
                  <Row
                    icon={<BadgeIcon />}
                    label="Employee ID"
                    value={profile?.employee_id}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Row icon={<PhoneIcon />} label="Phone" value={profile?.phone} />
                  <Row
                    icon={<WorkIcon />}
                    label="Designation"
                    value={profile?.designation}
                  />
                  <Row
                    icon={<BusinessIcon />}
                    label="Department"
                    value={profile?.department}
                  />
                  <Row
                    icon={<LanguageIcon />}
                    label="Default Language"
                    value={profile?.default_language_name}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Data Access
                </Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Row
                  icon={<CalendarIcon />}
                  label="Default Year"
                  value={profile?.default_year_name}
                />
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Accessible Regions
                  </Typography>
                  <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {profile?.accessible_regions?.length ? (
                      profile.accessible_regions.map((r: any) => (
                        <Chip key={r.id} label={r.name} size="small" variant="outlined" />
                      ))
                    ) : (
                      <Chip label="All Regions" size="small" color="success" variant="outlined" />
                    )}
                  </Box>
                </Box>
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Accessible Districts
                  </Typography>
                  <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {profile?.accessible_districts?.length ? (
                      profile.accessible_districts.map((d: any) => (
                        <Chip key={d.id} label={d.name} size="small" variant="outlined" />
                      ))
                    ) : (
                      <Chip label="All Districts" size="small" color="success" variant="outlined" />
                    )}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Profile;
